import React, { useState } from 'react';
import styled from 'styled-components';
import MotionLiquidButton from '../../components/MotionLiquidButton';
import LiquidButton from '../../components/LiquidButton';
import RoundedInput from '../../components/RoundedInput';
import ProgressIndicators from '../../components/ProgressIndicators';

const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Title = styled.h1`
    color: white;
    text-align: center;
    margin-bottom: 40px;
    font-size: 2.5rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const ShowcaseGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 30px;
    width: 100%;
    max-width: 1200px;
`;

const ShowcaseCard = styled.div`
    padding: 30px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const SectionTitle = styled.h2`
    color: #333;
    margin-bottom: 20px;
    font-size: 1.5rem;
    border-bottom: 2px solid #667eea;
    padding-bottom: 10px;
`;

const ButtonGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
`;

const ButtonLabel = styled.div`
    text-align: center;
    margin-bottom: 10px;
    font-weight: 600;
    color: #555;
    font-size: 0.9rem;
`;

const InputGroup = styled.div`
    margin-bottom: 20px;
`;

const InputLabel = styled.label`
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #555;
`;

const Description = styled.p`
    color: #666;
    margin-bottom: 20px;
    line-height: 1.6;
`;

const AnimationCounter = styled.div`
    text-align: center;
    margin-top: 15px;
    font-size: 0.9rem;
    color: #888;
`;

const SampleCard = styled.div`
    padding: 20px;
    margin-bottom: 15px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const GradientCard = styled(SampleCard)`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
`;

const LightCard = styled(SampleCard)`
    background: #f8f9fa;
`;

const InfoBox = styled.div`
    margin-top: 20px;
    padding: 15px;
    background: #e3f2fd;
    border-radius: 8px;
    border: 1px solid #bbdefb;
`;

const ComponentShowcase: React.FC = () => {
    const [motionClickCount, setMotionClickCount] = useState<Record<string, number>>({});
    const [cssClickCount, setCssClickCount] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const [emailValue, setEmailValue] = useState('');
    const [progressValue, setProgressValue] = useState(3);
    const [progressTotal] = useState(10);

    // Motion button variants
    const motionVariants: Array<'default' | 'bounce' | 'ripple' | 'blast' | 'glow' | 'shake'> = [
        'default',
        'bounce',
        'ripple',
        'blast',
        'glow',
        'shake',
    ];

    const handleMotionButtonClick = (variant: string) => {
        setMotionClickCount((prev) => ({
            ...prev,
            [variant]: (prev[variant] || 0) + 1,
        }));
    };

    const handleCssButtonClick = () => {
        setCssClickCount((prev) => prev + 1);
    };

    return (
        <Container>
            <Title>Component Showcase</Title>

            <ShowcaseGrid>
                {/* Motion Liquid Buttons */}
                <ShowcaseCard>
                    <SectionTitle>Motion Liquid Buttons</SectionTitle>
                    <Description>
                        Interactive buttons with Framer Motion animations. Each variant has unique
                        click animations with CSS hover effects to avoid animation conflicts.
                    </Description>

                    <ButtonGrid>
                        {motionVariants.map((variant) => (
                            <div key={variant}>
                                <ButtonLabel>
                                    {variant.charAt(0).toUpperCase() + variant.slice(1)}
                                </ButtonLabel>
                                <MotionLiquidButton
                                    variant={variant}
                                    onClick={() => handleMotionButtonClick(variant)}
                                >
                                    Click Me!
                                </MotionLiquidButton>
                                <AnimationCounter>
                                    Clicked: {motionClickCount[variant] || 0} times
                                </AnimationCounter>
                            </div>
                        ))}
                    </ButtonGrid>
                </ShowcaseCard>

                {/* CSS Liquid Button */}
                <ShowcaseCard>
                    <SectionTitle>CSS Liquid Button</SectionTitle>
                    <Description>
                        Traditional CSS-based button with hover effects and smooth transitions.
                        Features a liquid ripple effect on hover.
                    </Description>

                    <div style={{ textAlign: 'center' }}>
                        <ButtonLabel>CSS Hover Effect</ButtonLabel>
                        <LiquidButton onClick={handleCssButtonClick}>
                            Hover & Click Me!
                        </LiquidButton>
                        <AnimationCounter>Clicked: {cssClickCount} times</AnimationCounter>
                    </div>
                </ShowcaseCard>

                {/* Input Components */}
                <ShowcaseCard>
                    <SectionTitle>Input Components</SectionTitle>
                    <Description>
                        Rounded input fields with modern styling and smooth focus transitions.
                        Support for different input types and validation states.
                    </Description>

                    <InputGroup>
                        <InputLabel htmlFor="text-input">Text Input</InputLabel>
                        <RoundedInput
                            id="text-input"
                            type="text"
                            placeholder="Enter some text..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                    </InputGroup>

                    <InputGroup>
                        <InputLabel htmlFor="email-input">Email Input</InputLabel>
                        <RoundedInput
                            id="email-input"
                            type="email"
                            placeholder="Enter your email..."
                            value={emailValue}
                            onChange={(e) => setEmailValue(e.target.value)}
                        />
                    </InputGroup>

                    <InputGroup>
                        <InputLabel htmlFor="disabled-input">Disabled Input</InputLabel>
                        <RoundedInput
                            id="disabled-input"
                            type="text"
                            placeholder="This input is disabled"
                            disabled
                        />
                    </InputGroup>
                </ShowcaseCard>

                {/* Progress Indicators - All Variants */}
                <ShowcaseCard>
                    <SectionTitle>Progress Indicators - All Variants</SectionTitle>
                    <Description>
                        Complete collection of progress indicator styles. Each type provides a
                        different visual approach to showing completion with customizable variants,
                        colors, and animations.
                    </Description>

                    <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Bar Progress</h4>
                        <ProgressIndicators
                            currentIndex={progressValue - 1}
                            total={progressTotal}
                            type="bar"
                            variant="default"
                            color="purple"
                        />
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Circle Progress</h4>
                        <ProgressIndicators
                            currentIndex={progressValue - 1}
                            total={progressTotal}
                            type="circle"
                            variant="default"
                            color="blue"
                            size="100px"
                        />
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Steps Progress</h4>
                        <ProgressIndicators
                            currentIndex={progressValue - 1}
                            total={progressTotal}
                            type="steps"
                            variant="default"
                            color="green"
                        />
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Dots Progress</h4>
                        <ProgressIndicators
                            currentIndex={progressValue - 1}
                            total={progressTotal}
                            type="dots"
                            variant="thick"
                            color="orange"
                            animated
                        />
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Liquid Progress</h4>
                        <ProgressIndicators
                            currentIndex={progressValue - 1}
                            total={progressTotal}
                            type="liquid"
                            variant="default"
                            color="gradient"
                            animated
                        />
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Wave Progress</h4>
                        <ProgressIndicators
                            currentIndex={progressValue - 1}
                            total={progressTotal}
                            type="wave"
                            variant="default"
                            color="red"
                            animated
                        />
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <LiquidButton
                            onClick={() =>
                                setProgressValue((prev) => (prev + 1) % (progressTotal + 1))
                            }
                        >
                            Update All Indicators ({progressValue}/{progressTotal})
                        </LiquidButton>
                    </div>
                </ShowcaseCard>

                {/* Card Component */}
                <ShowcaseCard>
                    <SectionTitle>Card Component</SectionTitle>
                    <Description>
                        Versatile card container with clean styling and subtle shadows. Perfect for
                        grouping related content and creating visual hierarchy.
                    </Description>

                    <LightCard>
                        <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Sample Card</h4>
                        <p style={{ margin: 0, color: '#666' }}>
                            This is an example of the Card component with custom styling. It
                            provides a clean container for content with consistent spacing.
                        </p>
                    </LightCard>

                    <GradientCard>
                        <h4 style={{ margin: '0 0 10px 0' }}>Gradient Card</h4>
                        <p style={{ margin: 0, opacity: 0.9 }}>
                            Cards can be styled with gradients and custom colors to match your
                            design.
                        </p>
                    </GradientCard>
                </ShowcaseCard>

                {/* Animation Information */}
                <ShowcaseCard>
                    <SectionTitle>Animation Details</SectionTitle>
                    <Description>
                        All animations have been optimized for performance and user experience:
                    </Description>

                    <ul style={{ color: '#666', lineHeight: 1.8, paddingLeft: '20px' }}>
                        <li>
                            <strong>Motion Buttons:</strong> 50% faster animations (0.3s - 0.6s
                            duration)
                        </li>
                        <li>
                            <strong>Hover Effects:</strong> CSS-only for smooth performance
                        </li>
                        <li>
                            <strong>No Conflicts:</strong> Hover and click animations are separated
                        </li>
                        <li>
                            <strong>Spring Physics:</strong> Natural motion with spring transitions
                        </li>
                        <li>
                            <strong>Responsive:</strong> All components work across different screen
                            sizes
                        </li>
                    </ul>
                </ShowcaseCard>

                {/* Usage Instructions */}
                <ShowcaseCard>
                    <SectionTitle>Usage Instructions</SectionTitle>
                    <Description>
                        Try interacting with the components above to see their behaviors:
                    </Description>

                    <ul style={{ color: '#666', lineHeight: 1.8, paddingLeft: '20px' }}>
                        <li>
                            <strong>Motion Buttons:</strong> Click to see unique animations for each
                            variant
                        </li>
                        <li>
                            <strong>CSS Button:</strong> Hover to see ripple effect, click for
                            interaction
                        </li>
                        <li>
                            <strong>Inputs:</strong> Focus and type to see transition effects
                        </li>
                        <li>
                            <strong>Cards:</strong> Versatile containers for organizing content
                        </li>
                    </ul>

                    <InfoBox>
                        <strong style={{ color: '#1976d2' }}>💡 Tip:</strong>
                        <span style={{ color: '#555', marginLeft: '8px' }}>
                            All components are built with TypeScript and styled-components for type
                            safety and maintainability.
                        </span>
                    </InfoBox>
                </ShowcaseCard>
            </ShowcaseGrid>
        </Container>
    );
};

export default ComponentShowcase;
